import { toast } from "@ronin/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { authStatusQuery } from "../api.js";
import { requestConflictReview } from "../lib/conflict-review.js";
import { isNative } from "../platform/native.js";
import {
  offlineStore,
  setOfflineTimeZone,
  startOfflineSync,
  subscribeOfflineStore,
} from "../platform/offline/index.js";
import { useTimeZone } from "./settings/use-settings.js";

const plural = (n: number, one: string, many: string) => (n === 1 ? `1 ${one}` : `${n} ${many}`);

/**
 * spec11 FR7, FR10, FR13, FR17 (app only): runs the sync loop while signed in, refreshes the
 * note lists when the offline copy changes, and reports what a sync did.
 */
export function useOfflineSync() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const timeZone = useTimeZone();

  useEffect(() => setOfflineTimeZone(timeZone), [timeZone]);

  useEffect(() => {
    if (!isNative) return;
    // Lists, notebooks, search and the trash read the offline copy (the open note is the
    // editor's).
    let unsubscribeStore = () => {};
    const watchStore = () => {
      unsubscribeStore();
      unsubscribeStore =
        offlineStore()?.subscribe(
          () =>
            void queryClient.invalidateQueries({
              predicate: (q) => q.queryKey[0] === "notes" && q.queryKey[1] !== "note",
            }),
        ) ?? (() => {});
    };
    const unsubscribeStores = subscribeOfflineStore(watchStore);
    watchStore();

    const stop = startOfflineSync((event) => {
      if (event.kind === "unauthorized") {
        // The store and its outbox stay; signing in again as the same user continues.
        queryClient.removeQueries({ queryKey: authStatusQuery.queryKey });
        void navigate({ to: "/login" });
        return;
      }
      if (event.kind === "incompatible") {
        // The version check shows the update screen (spec08 FR9).
        void queryClient.invalidateQueries({ queryKey: authStatusQuery.queryKey });
        return;
      }
      if (event.kind !== "ok") return;
      // FR9: a notebook change the server refused was dropped; the tree shows its state.
      for (const reason of event.refused) {
        toast.error(`A notebook change was refused: ${reason}`);
      }
      if (event.backOnline) {
        toast.success(`Back online · ${plural(event.sent, "note", "notes")} synced`);
      }
      if (event.conflicts.length > 0) {
        toast(`${plural(event.conflicts.length, "note", "notes")} had a conflict`, {
          id: "note-conflicts",
          description: "Both versions were kept.",
          duration: Number.POSITIVE_INFINITY,
          action: {
            label: "Review",
            onClick: () => {
              requestConflictReview();
              void navigate({ to: "/notes" });
            },
          },
        });
      }
    });
    return () => {
      stop();
      unsubscribeStores();
      unsubscribeStore();
    };
  }, [queryClient, navigate]);
}
